<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading
            :module-id="access.module_id"
            :profile-id="access.profile_id"
            :reference="access.view_reference"
            summary-only
          />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions">
              <TabHeaderActions />
            </div>
            <div class="list-view-heading__controls-end">
              <b-button variant="link"
                        class="list-view-icon-btn"
                        :class="{ 'list-view-icon-btn--active': filtersOpen }"
                        v-b-tooltip.hover.bottom
                        :title="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        :aria-label="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        @click="filtersOpen = !filtersOpen">
                <AppIcon name="filter" :fill="filtersOpen" />
              </b-button>
              <b-dropdown right
                          no-caret
                          variant="link"
                          boundary="window"
                          toggle-class="list-view-icon-btn list-view-icon-btn--primary"
                          v-b-tooltip.hover.bottom
                          title="Agregar Remito de Compra">
                <template #button-content>
                  <AppIcon name="add" label="Agregar Remito de Compra" />
                </template>
                <b-dropdown-item @click="add()">
                  <AppIcon name="add" /> Alta remito normal
                </b-dropdown-item>
                <b-dropdown-item @click="addFromPurchaseOrder()">
                  <AppIcon name="cart" /> Alta desde orden de compra
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </div>
        </section>

        <b-collapse v-model="filtersOpen">
          <div class="list-view-toolbar__filters">
            <div class="list-view-filter" style="width: 150px;">
              <label class="filter-label">Desde</label>
              <b-form-input type="date" size="sm" v-model="filters.date_start"></b-form-input>
            </div>
            <div class="list-view-filter" style="width: 150px;">
              <label class="filter-label">Hasta</label>
              <b-form-input type="date" size="sm" v-model="filters.date_end"></b-form-input>
            </div>
            <div class="list-view-filter" style="flex: 1 1 230px; max-width: 340px;">
              <label class="filter-label">Proveedores</label>
              <v-select :options="arr.filters.suppliers"
                        v-model="filters.suppliers"
                        placeholder="Todos"
                        :multiple="true"
                        :select-on-tab="true"></v-select>
            </div>
            <b-button variant="outline-dark" size="sm" @click="filterPurchaseRemits(true)" v-b-tooltip.hover title="Aplicar filtros" style="height: 31px; width: 31px; padding: 0; flex-shrink: 0;">
              <AppIcon name="filter" />
            </b-button>
          </div>
        </b-collapse>
      </b-card>

      <b-row>
        <b-col lg="12" id="view_list_purchase_remits">
          <b-card no-body>
            <b-row no-gutters>
              <b-col sm="12">
                <b-table class="app-table table-purchase-remits-custom"
                        ref="table"
                        responsive
                        :stacked="isMobileViewport ? 'md' : false"
                        sticky-header="60vh"
                        :hover="true"
                        :small="true"
                        :fixed="true"
                        :items="table.items"
                        :fields="table.fields"
                        :current-page="table.currentPage"
                        :per-page="table.perPage"
                        @row-clicked="onRowClicked"
                        :tbody-tr-class="rowClass"
                        :busy="table.isBusy"                      
                        v-if="table.items.length || table.isBusy">                       
                    
                    <template v-slot:table-colgroup="scope">
                      <col
                        v-for="field in scope.fields"                    
                        :key="field.key"
                        :style="{ width: field.width }"
                      >
                    </template> 

                    <template v-slot:table-busy>
                      <div class="text-center text-danger my-2">
                        <b-spinner class="align-middle"></b-spinner>
                        <strong>Cargando...</strong>
                      </div>
                    </template>
                    
                    <template v-slot:cell(business)="data">
                      {{data.item.points_sales.business.reference}}
                    </template>                    
                    
                    <template v-slot:cell(voucher)="data">
                      <div v-if="data.item.type_voucher && data.item.point_sale">
                        <b-badge variant="dark" v-if="data.item.points_sales">
                          {{data.item.points_sales.point_sale}} - {{data.item.points_sales.name}}
                        </b-badge> <br>
                        {{data.item.type_voucher.name}}
                        {{data.item.point_sale.toString().padStart(4,'0') + '-' + data.item.number.toString().padStart(8,'0')}}
                        <b-badge variant="success" class="ml-1"
                                 v-if="isRemitLinked(data.item)"
                                 v-b-tooltip.hover title="Todos los ítems tienen factura vinculada">
                          <AppIcon name="check2-circle" /> Factura vinculada
                        </b-badge>
                        <b-badge variant="warning" class="ml-1"
                                 v-else-if="isRemitPartiallyLinked(data.item)"
                                 v-b-tooltip.hover title="Algunos ítems aún no tienen factura. Usá 'Cargar Factura' para completarlos.">
                          <AppIcon name="warning" /> Parcialmente facturado
                        </b-badge>
                        <b-badge variant="info" class="ml-1"
                                 v-if="data.item.details && data.item.details.length && hasRemitPrices(data.item) && !isRemitLinked(data.item) && !isRemitPartiallyLinked(data.item)"
                                 v-b-tooltip.hover title="El remito tiene precios cargados pero aún no tiene factura. Usá 'Cargar Factura' para generarla.">
                          <AppIcon name="clock" /> Pendiente de facturar
                        </b-badge>
                        <b-badge variant="danger" class="ml-1"
                                 v-if="data.item.details && data.item.details.length && !hasRemitPrices(data.item)"
                                 v-b-tooltip.hover title="Hay ítems sin precio de costo. Editá el remito para completarlos antes de facturar.">
                          <AppIcon name="warning" /> Sin precios
                        </b-badge>
                      </div>
                    </template>
                    
                    <template v-slot:cell(date)="data">
                      {{moment(data.item.date).format('DD/MM/YYYY')}}
                    </template>
                    
                    <template v-slot:cell(suppliers_id)="data">  
                      <div v-if="data.item.supplier">            
                        <b-avatar :src="data.item.supplier.image"
                                  v-if="data.item.supplier.image"
                                  size="25px">
                        </b-avatar>
                        <b-avatar v-else 
                                  :text="avatarFromName(data.item.supplier.name).text"
                                  :style="avatarFromName(data.item.supplier.name).style"
                                  size="25px">
                        </b-avatar>                                                     
                        &nbsp;<b>{{data.item.supplier.name}}</b>   
                        
                        <AppIcon name="circle-fill" v-if="!data.item.supplier.active" class="ml-2 text-danger" v-b-tooltip.hover title="Proveedor INACTIVO" />
                      </div>
                    </template>

                    <template v-slot:cell(observations)="data">
                      <span v-if="data.item.file">
                        <b-link target="blank" :href="data.item.file" v-b-tooltip.hover title="Ver documento adjunto" class="text-center">
                          <AppIcon name="paperclip" />
                        </b-link>
                      </span>                                  
                      <span v-if="data.item.observations" v-b-tooltip.hover :title="data.item.observations" class="mr-1 text-center">
                        <span v-if="isMobileViewport">{{data.item.observations}}</span>
                        <AppIcon v-else name="chat-square-text" />
                      </span>                                                           
                    </template>
                    
                    <template v-slot:cell(f_action)="data">
                      <b-dropdown right
                                  no-caret
                                  size="sm"
                                  variant="link"
                                  boundary="window"
                                  :toggle-class="isMobileViewport ? 'purchase-remits-action-toggle-labeled' : 'app-table-action-toggle'"
                                  :class="{ 'pull-right': !isMobileViewport }">                        
                        <template #button-content>
                          <AppIcon name="more_vert" :label="isMobileViewport ? 'Opciones' : 'Acciones'" :class="{ 'mr-1': isMobileViewport }" />
                          <span v-if="isMobileViewport">Opciones</span>
                        </template>
                        <b-dropdown-item @click="sendPrinter(data.item)">
                          <AppIcon name="print" /> Imprimir
                        </b-dropdown-item>                                        
                        <b-dropdown-item @click="generateInvoice(data.item)" v-if="!isRemitLinked(data.item)">                          
                          <AppIcon name="receipt" /> Cargar Factura
                        </b-dropdown-item>                                  
                        <b-dropdown-header v-if="canShowActions(data.item)">Acciones</b-dropdown-header>
                        <b-dropdown-item @click="edit(data.item)" v-if="!hasRemitLinkedItems(data.item)">
                          <AppIcon name="edit" /> Editar
                        </b-dropdown-item>                          
                        <b-dropdown-item @click="remove(data.item)" v-if="access.elements.deletePurchaseRemits && !hasRemitLinkedItems(data.item)">
                          <AppIcon name="delete" class="text-danger" /> Eliminar
                        </b-dropdown-item>
                      </b-dropdown>
                    </template>
                </b-table>
                <EmptyState v-else icon="receipt" title="No se encontraron remitos de compra" description="Ajustá los filtros o el rango de fechas para ampliar la búsqueda, o cargá un remito nuevo con el botón Agregar." />

                <div class="app-table-footer purchase-remits-table-footer">
                  <span class="list-view-toolbar__count" v-if="table.tablePaginate">{{ table.tablePaginate.total }} registros</span>
                  <export-excel
                    class="list-view-link"
                    :data="arr.export"
                    worksheet="Listado de Remitos de Compras"
                    name="list-purchase-remits.xls">
                    <AppIcon name="download" class="mr-1" /> Exportar
                  </export-excel>
                </div>
              </b-col>
            </b-row>
          </b-card>
        </b-col>
      </b-row> 

      <b-sidebar
          id="sidebar-purchase-remit-detail"
          v-model="detailDrawer"
          right
          shadow
          backdrop
          title="Detalle de Remito de Compra"
          width="420px"
          @hidden="hideSub">
        <div class="list-view-drawer" v-if="itemSelected">
          <div class="list-view-drawer__header">
            <div class="list-view-drawer__title">
              <span class="list-view-drawer__code" v-if="itemSelected.type_voucher && itemSelected.point_sale">
                {{itemSelected.type_voucher.name}} {{itemSelected.point_sale.toString().padStart(4,'0') + '-' + itemSelected.number.toString().padStart(8,'0')}}
              </span>
              <div v-if="itemSelected.supplier">{{itemSelected.supplier.name}}</div>
              <div class="list-view-drawer__meta" v-if="itemSelected.date">
                {{moment(itemSelected.date).format('DD/MM/YYYY')}}
              </div>
            </div>
            <div class="list-view-drawer__nav">
              <b-button size="sm"
                        variant="outline-secondary"
                        :disabled="!canGoPrevRemit"
                        v-b-tooltip.hover
                        title="Remito anterior"
                        @click="goPrevRemit()">
                <AppIcon name="chevron-up" />
              </b-button>
              <b-button size="sm"
                        variant="outline-secondary"
                        :disabled="!canGoNextRemit"
                        v-b-tooltip.hover
                        title="Remito siguiente"
                        @click="goNextRemit()">
                <AppIcon name="chevron-down" />
              </b-button>
            </div>
          </div>

          <div class="list-view-drawer__stats">
            <div class="list-view-drawer__stat">
              <span class="list-view-drawer__stat-label">Items</span>
              <span class="list-view-drawer__stat-value">{{tableSub.items.length}}</span>
            </div>
            <div class="list-view-drawer__stat" :class="{ 'list-view-drawer__stat--warning': !hasRemitPrices(itemSelected) || isRemitPartiallyLinked(itemSelected) }">
              <span class="list-view-drawer__stat-label">Estado</span>
              <span class="list-view-drawer__stat-value">{{getRemitBillingStatusLabel(itemSelected)}}</span>
            </div>
          </div>

          <div>
            <div class="list-view-drawer__section-title">
              <AppIcon name="list-ul" /> Detalle
            </div>

            <b-table class="app-table app-table--compact"
                    responsive
                    stacked
                    :hover="true"
                    :small="true"
                    :fixed="false"
                    :items="tableSub.items"
                    :fields="tableSub.fields"
                    v-if="tableSub.items.length">

              <template v-slot:cell(quantity)="row"> 
                {{row.item.type_balance}} {{row.item.quantity}}
                <span v-if="row.item.unit_measurement">{{row.item.unit_measurement.reference}}</span>
              </template>

              <template v-slot:cell(product)="row">
                <div class="text-truncate" v-b-tooltip.hover :title="getDetailName(row.item)" v-html="getDetailName(row.item)"></div>
              </template>
            </b-table>
            <EmptyState v-else icon="list-ul" title="Sin items cargados" />
          </div>

          <b-alert v-for="difference in getRemitPurchaseDifferences(itemSelected)"
                   :key="'remit-purchase-difference-' + difference.purchase.id"
                   show
                   variant="info"
                   class="mb-3">
            <AppIcon name="info-circle" class="mr-1" />
            <b>Mensaje informativo:</b> el importe neto de la factura {{ getPurchaseReference(difference.purchase) }} ({{ formatRemitPurchaseAmount(difference.invoiceAmount) }}) difiere del importe neto de los ítems de este remito vinculados a esta factura ({{ formatRemitPurchaseAmount(difference.remitAmount) }}).
          </b-alert>

          <b-button v-if="tableSub.items.length"
                    block
                    size="sm"
                    variant="outline-secondary"
                    class="text-left"
                    @click="openDetail()">
            <AppIcon name="visibility" class="mr-1" /> Ver detalle completo
          </b-button>

          <div class="list-view-drawer__actions">
            <b-button size="sm" variant="outline-secondary" @click="sendPrinter(itemSelected)">
              <AppIcon name="print" class="mr-1" /> Imprimir
            </b-button>
            <b-button size="sm" variant="outline-secondary" @click="generateInvoice(itemSelected)" v-if="!isRemitLinked(itemSelected)">
              <AppIcon name="receipt" class="mr-1" /> Cargar Factura
            </b-button>
            <b-button size="sm" variant="outline-secondary" @click="edit(itemSelected)" v-if="!hasRemitLinkedItems(itemSelected)">
              <AppIcon name="edit" class="mr-1" /> Editar
            </b-button>
          </div>
        </div>

        <template #footer>
          <div class="list-view-drawer__footer" v-if="itemSelected && access.elements.deletePurchaseRemits && !hasRemitLinkedItems(itemSelected)">
            <b-button size="sm" variant="outline-danger" block @click="remove(itemSelected)">
              <AppIcon name="delete" class="mr-1" /> Eliminar
            </b-button>
          </div>
        </template>
      </b-sidebar>

      <b-row>
        <b-col lg="12" id="view_footer_purchase_remits">  
          <b-card body-class="py-2">
            <div class="list-view-footer">
              <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
                <AppIcon name="back" />
                Volver
              </b-button>

              <nav v-if="table.tablePaginate">
                <b-pagination class="mb-0"
                              size="sm"
                              pills=""
                              :total-rows="table.tablePaginate.total"
                              :per-page="table.tablePaginate.per_page"
                              v-model="table.currentPage"
                              @input="filterPurchaseRemits()" />
              </nav>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!--
      ###########################
      ######### PRINTER #########
      ###########################
      -->
      <Printer v-if="crud.print.id" 
              :reference="crud.print.reference" 
              :registerID="crud.print.id"
              :key="crud.print.key"/>

      <!-- ########################### -->
      <!-- #####     MODALES     ##### -->
      <!-- ########################### -->

      <PurchaseRemitFormModal ref="purchaseRemitForm"
                              @saved="filterPurchaseRemits()" />

      <!-- CRUD DETAILS -->
      <b-modal v-model="modal.detail.active"
              size="xl"
              dialog-class="remit-detail-modal-wide"
              :key="modal.detail._id"
              :id="modal.detail._id">                  
        <div slot="modal-header" minimize class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="visibility" /></span>
          <h5 class="app-modal-header__title">{{this.modal.detail.title}}</h5>
        </div>

        <b-row>
          <b-col sm="12">                     
            <b-tabs active-nav-item-class="font-weight-bold text-uppercase text-dark">
              <b-tab>
                <template #title><AppIcon name="description" class="mr-1" /> General</template>
                <div class="app-modal-detail-list" v-if="itemSelected">
                  <b-alert v-for="difference in getRemitPurchaseDifferences(itemSelected)"
                           :key="'remit-detail-purchase-difference-' + difference.purchase.id"
                           show
                           variant="info"
                           class="mb-3">
                    <AppIcon name="info-circle" class="mr-1" />
                    <b>Mensaje informativo:</b> el importe neto de la factura {{ getPurchaseReference(difference.purchase) }} ({{ formatRemitPurchaseAmount(difference.invoiceAmount) }}) difiere del importe neto de los ítems de este remito vinculados a esta factura ({{ formatRemitPurchaseAmount(difference.remitAmount) }}).
                  </b-alert>
                  <div class="app-modal-detail-list__row">
                    <span class="app-modal-detail-list__label">ID</span>
                    <span class="app-modal-detail-list__value"><b>#{{this.itemSelected.id}}</b></span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.points_sales && itemSelected.points_sales.business">
                    <span class="app-modal-detail-list__label">Empresa</span>
                    <span class="app-modal-detail-list__value">{{this.itemSelected.points_sales.business.reference}}</span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.points_sales">
                    <span class="app-modal-detail-list__label">Punto de Venta Imputación</span>
                    <span class="app-modal-detail-list__value">{{this.itemSelected.points_sales.point_sale}} - {{this.itemSelected.points_sales.name}}</span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.type_voucher && itemSelected.point_sale">
                    <span class="app-modal-detail-list__label">Comprobante</span>
                    <span class="app-modal-detail-list__value"><b>{{this.itemSelected.type_voucher.name + ' '  + this.itemSelected.point_sale.toString().padStart(4,'0') + '-' + this.itemSelected.number.toString().padStart(8,'0')}}</b></span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.staff">
                    <span class="app-modal-detail-list__label">Emisor</span>
                    <span class="app-modal-detail-list__value">{{itemSelected.staff.name}}</span>
                  </div>
                  <div class="app-modal-detail-list__row">
                    <span class="app-modal-detail-list__label">Fecha</span>
                    <span class="app-modal-detail-list__value">{{moment(this.itemSelected.date).format('DD MMMM YYYY')}}</span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.supplier">
                    <span class="app-modal-detail-list__label">Proveedor</span>
                    <span class="app-modal-detail-list__value">{{this.itemSelected.supplier.name}}</span>
                  </div>
                  <div class="app-modal-detail-list__row">
                    <span class="app-modal-detail-list__label">Estado factura</span>
                    <span class="app-modal-detail-list__value">
                      <b-badge variant="success"
                               v-if="isRemitLinked(itemSelected)"
                               v-b-tooltip.hover
                               title="Todos los ítems tienen factura vinculada">
                        <AppIcon name="check2-circle" /> Factura vinculada
                      </b-badge>
                      <b-badge variant="warning"
                               v-else-if="isRemitPartiallyLinked(itemSelected)"
                               v-b-tooltip.hover
                               title="Algunos ítems aún no tienen factura. Usá 'Cargar Factura' para completarlos.">
                        <AppIcon name="warning" /> Parcialmente facturado
                      </b-badge>
                      <b-badge variant="info"
                               v-else-if="itemSelected.details && itemSelected.details.length && hasRemitPrices(itemSelected)"
                               v-b-tooltip.hover
                               title="El remito tiene precios cargados pero aún no tiene factura. Usá 'Cargar Factura' para generarla.">
                        <AppIcon name="clock" /> Pendiente de facturar
                      </b-badge>
                      <b-badge variant="danger"
                               v-else-if="itemSelected.details && itemSelected.details.length && !hasRemitPrices(itemSelected)"
                               v-b-tooltip.hover
                               title="Hay ítems sin precio de costo. Editá el remito para completarlos antes de facturar.">
                        <AppIcon name="warning" /> Sin precios
                      </b-badge>
                      <span v-else>Sin ítems</span>
                    </span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.observations">
                    <span class="app-modal-detail-list__label">Observaciones</span>
                    <span class="app-modal-detail-list__value">{{this.itemSelected.observations}}</span>
                  </div>
                </div>
              </b-tab>    

              <b-tab>
                <template #title><AppIcon name="list-ul" class="mr-1" /> Items</template>
                <b-table class="mb-0 app-table app-table--compact table-full-detail-purchase-remits"
                        responsive
                        :stacked="isMobileViewport ? 'md' : false"
                        :hover="true"
                        :small="true"
                        :fixed="true"
                        :items="tableDetail.items"
                        :fields="tableDetail.fields"
                        v-if="tableDetail.items.length">                       
                    
                  <template v-slot:table-colgroup="scope">
                    <col
                      v-for="field in scope.fields"                    
                      :key="field.key"
                      :style="{ width: field.width }"
                    >
                  </template>           

                  <template v-slot:cell(product)="data">                           
                    <div class="purchase-remits-detail-item">
                      <div class="purchase-remits-detail-item__code" v-html="getDetailCode(data.item)"></div>
                      <div class="purchase-remits-detail-item__name text-truncate" v-b-tooltip.hover :title="getDetailName(data.item)" v-html="getDetailName(data.item)"></div>
                    </div>
                  </template>

                  <template v-slot:cell(stock_control)="data">
                    <b-badge :variant="stockBadgeVariant(data.item)" :title="stockBadgeTitle(data.item)">
                      {{ stockBadgeLabel(data.item) }}
                    </b-badge>
                  </template>

                  <template v-slot:cell(deposit)="data">   
                    <div class="purchase-remits-detail-location" v-if="data.item.location">
                      <DepositLocationLabel :location="data.item.location" />
                    </div>
                  </template>

                  <template v-slot:cell(purchase)="data">
                    <span v-if="data.item.purchase">
                      {{getPurchaseReference(data.item.purchase)}}
                    </span>
                    <span v-else class="text-muted">Sin factura</span>
                  </template>

                  <template v-slot:cell(quantity)="data">   
                    {{data.item.quantity}}
                    <span v-if="data.item.unit_measurement">{{data.item.unit_measurement.reference}}</span>
                  </template>

                  <template v-slot:cell(price_cost)="data">   
                    {{Intl.NumberFormat('es-AR',{style:'currency',currency:getCurrency}).format(data.item.price_cost)}}                                    
                  </template>

                  <template v-slot:cell(price_total)="data">   
                    {{Intl.NumberFormat('es-AR',{style:'currency',currency:getCurrency}).format(parseFloat(data.item.quantity) * parseFloat(data.item.price_cost))}}                                    
                  </template>
                </b-table>
                <EmptyState v-else icon="list-ul" title="No se encontraron items" />
              </b-tab>              
            </b-tabs>
          </b-col>
        </b-row>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="modal.detail.active=false">
            <AppIcon name="x" class="mr-1" />Cerrar
          </b-button>
        </div>
      </b-modal>


    </b-col>
  </b-row>
</template>
<script>
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import ErrorToken from '@/handler/errorToken'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper' 
  import Storage from '@/handler/storageSession' 
  import moment from 'moment'
  import Printer from '@/components/inc/printer/printer'  
  import Function from '@/handler/funciones'
  import StorageLocal from '@/handler/storageLocal'
  import PurchaseRemitFormModal from './inc/PurchaseRemitFormModal'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'

  export default { 
    components: {
      PurchaseRemitFormModal,
      Printer,
      TabHeaderActions,
    },       
    data: () => {
      return {
        access: {
          module_id: Modules.REMITOS_COMPRAS,
          profile_id: Profiles.PERSONAL,
          view_reference: 'purchaseRemits',
          elements: {
            deletePurchaseRemits: true
          }
        },
        table : {
          items: [],
          fields: [],
          filter: null,
          mostrarFiltros: true,
          currentPage: 1,
          perPage: 0,
          isBusy: false,
          tablePaginate: null,          
        },   
        tableSub : {
          items: [],
          fields: []
        },    
        tableDetail : {
          items: [],
          fields: []
        },             
        crud: {
          form: {
            id: 0,
          },
          print: {
            reference: '',
            id: 0,
            key: 0,
          }                        
        },          
        modal: {
          detail: {
            active: false,
            title: ''
          },
        },                     
        arr: {
          purchaseRemits: [],   
          export: [],          
          filters : {
            suppliers: [],
          }
        },           
        filters: {
          date_start: '',
          date_end: '',
          suppliers: null,
        },  
        itemSelected: null,
        detailDrawer: false,
        filtersOpen: false,
        isMobileViewport: false,
      }
    },
    created () {
      /* Configurar permisos de vistas y elementos */
      Helper.hasAccessView(this.access)
      this.access.elements.deletePurchaseRemits = Helper.hasAccessElement(this.access, 'delete_purchase_remits') 
      /* Fin configuracion */
    },
    computed: {
      getCurrency(){
        return "ARS"
      },

      currentRemitIndex() {
        if(!this.itemSelected) return -1
        return this.table.items.findIndex(item => item.id === this.itemSelected.id)
      },
      canGoPrevRemit() {
        return this.currentRemitIndex > 0
      },
      canGoNextRemit() {
        return this.currentRemitIndex >= 0 && this.currentRemitIndex < this.table.items.length - 1
      },
    },
    mounted() {
      this.filtersOpen = StorageLocal.getValue('filters_open_crud_purchase_remits') == 'true'

      this.mobileMql = window.matchMedia('(max-width: 767.98px)')
      this.isMobileViewport = this.mobileMql.matches
      this.mobileMql.addEventListener('change', this.updateMobileViewport)

      this.loadFieldTable()
      this.filterLoad()
      this.filterPurchaseRemits()

      // Acción directa desde la paleta de comandos: abrir el alta al entrar
      // (query.mode: 'from_order' abre el alta desde orden de compra, ver
      // addFromPurchaseOrder(); cualquier otro valor u omitido, el alta
      // normal).
      if (this.$route.query.new == '1') {
        if (this.$route.query.mode == 'from_order') {
          this.addFromPurchaseOrder()
        } else {
          this.add()
        }
        // Limpia el query apenas se consume -- si no, la pestaña queda con
        // ?new=1 pegado para siempre y volver a ella (activated, keep-alive)
        // reabriría el alta de nuevo aunque el usuario solo quiera ver el
        // listado.
        this.$router.replace({ path: this.$route.path, query: {} }).catch(() => {})
      }

      this._start = true
    },
    beforeDestroy() {
      if(this.mobileMql) {
        this.mobileMql.removeEventListener('change', this.updateMobileViewport)
      }
    },
    activated() {
      if(this._start) {
        this._start = false
        return false
      }

      this.filterLoad()
      this.filterPurchaseRemits()

      // Misma acción directa que en mounted(): si la pestaña ya estaba
      // montada (keep-alive), "mounted" no vuelve a correr y la paleta de
      // comandos no abriría nada sin este chequeo acá también.
      if (this.$route.query.new == '1') {
        if (this.$route.query.mode == 'from_order') {
          this.addFromPurchaseOrder()
        } else {
          this.add()
        }
        // Limpia el query apenas se consume -- si no, la pestaña queda con
        // ?new=1 pegado para siempre y volver a ella (activated, keep-alive)
        // reabriría el alta de nuevo aunque el usuario solo quiera ver el
        // listado.
        this.$router.replace({ path: this.$route.path, query: {} }).catch(() => {})
      }
    },
    watch: {        
      filtersOpen(value) {
        StorageLocal.setValue('filters_open_crud_purchase_remits', value ? 'true' : 'false')
      },
      isMobileViewport() {
        this.loadFieldTable()
      },
    },        
    methods: {
      // CONFIGURACION
      updateMobileViewport(e) {
        this.isMobileViewport = e.matches
      },
      alignClass(desktopClass) {
        return this.isMobileViewport ? 'text-left' : desktopClass
      },
      avatarFromName(name) {
        return Function.avatarFromName(name)
      },
      confirmDialog(message, options) {
        var danger = !!options.danger
        var icon = options.icon || (danger ? 'warning' : 'help')
        var title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
          this.$createElement('AppIcon', { props: { name: icon } }),
          options.title
        ])
        return this.$bvModal.msgBoxConfirm(message, {
          title: title,
          size: options.size || 'sm',
          buttonSize: options.buttonSize || 'sm',
          okVariant: danger ? 'danger' : 'dark',
          okTitle: options.okTitle,
          cancelTitle: options.cancelTitle || 'Cancelar',
          footerClass: 'p-2',
          headerClass: danger ? 'app-confirm-header app-confirm-header--danger' : 'app-confirm-header',
          hideHeaderClose: false,
          centered: true
        })
      },
      loadFieldTable () {
        this.table.fields = []
        this.tableSub.fields = []
        this.tableDetail.fields = []

        // TABLE COMPROBANTES
        this.table.fields.push({key: 'business', label: 'Empresa', class:"align-middle text-left", width:"60px"})
        this.table.fields.push({key: 'voucher', label: 'Comprobante', class:"align-middle text-left", width:"150px"})
        this.table.fields.push({key: 'date', label: 'Fecha', class:"align-middle " + this.alignClass('text-center'), width:"90px"})
        this.table.fields.push({key: 'suppliers_id', label: 'Proveedor', class:"align-middle", width:"150px"})
        this.table.fields.push({key: 'observations', label: '', class:"align-middle " + this.alignClass('text-center'), width:"25px"})
        this.table.fields.push({key: 'f_action', label: this.isMobileViewport ? 'Acción' : '', class:"align-middle " + this.alignClass('text-right'), width:"70px"})

        // TABLE DETALLE
        this.tableSub.fields.push({key: 'quantity', label: 'Cantidad', class:"align-middle", width:"50px"})                
        this.tableSub.fields.push({key: 'product', label: 'Item', class:"align-middle", width:"150px"})  
        
        // TABLE DETALLE COMPLETO        
        this.tableDetail.fields.push({key: 'product', label: 'Item', class:"align-middle text-left", width:"250px"})                                         
        this.tableDetail.fields.push({key: 'stock_control', label: 'Stock', class:"align-middle " + this.alignClass('text-center'), width:"125px"})
        this.tableDetail.fields.push({key: 'deposit', label: 'Ubicación', class:"align-middle text-left", width:"100px"})          
        this.tableDetail.fields.push({key: 'purchase', label: 'Factura', class:"align-middle", width:"120px"})          
        this.tableDetail.fields.push({key: 'quantity', label: 'Cant.', class:"align-middle " + this.alignClass('text-right'), width:"60px"})            
        this.tableDetail.fields.push({key: 'price_cost', label: 'Precio', class:"align-middle " + this.alignClass('text-right'), width:"70px"})     
        this.tableDetail.fields.push({key: 'price_total', label: 'Total', class:"align-middle " + this.alignClass('text-right'), width:"80px"})     
      }, 
      onRowSelected(item) {         
        this.arr.purchaseRemits.forEach((element, index) => {
          this.table.items[index]._showDetails = false
          if(item.length){
            if(element.id == item[0].id) {  
              this.openSub(element)                             
            }
          }
        })
      },       
      onRowClicked(item, index, event) {
        if(!item) return

        if(event && event.target) {
          const ignoreTags = ['INPUT', 'BUTTON', 'A', 'SELECT', 'TEXTAREA', 'LABEL', 'SVG', 'PATH']
          if(ignoreTags.includes(event.target.tagName)) return
          if(event.target.closest('.dropdown')) return
        }

        this.openSub(item)
      },
      rowClass(item, type) {
        if(type !== 'row' || !item) return ''
        if(this.detailDrawer && this.itemSelected && item.id === this.itemSelected.id) {
          return 'app-table-row-active'
        }
        return ''
      },
      goPrevRemit() {
        if(!this.canGoPrevRemit) return
        this.openSub(this.table.items[this.currentRemitIndex - 1])
      },
      goNextRemit() {
        if(!this.canGoNextRemit) return
        this.openSub(this.table.items[this.currentRemitIndex + 1])
      },

      // EXPORT
      prepareExport(data) {             
        this.arr.export = []
        data.forEach(element => {
          var businessID = 0
          var businessName = ""          
          var suppliersID = 0
          var suppliersCode = ""
          var suppliersName = ""
          var typeVoucherID = 0
          var typeVoucherName = ""
          var pointSalesID = 0
          var pointSalesName = ""

          if(element.points_sales && element.points_sales.business) {
            businessID = element.points_sales.business.id            
            businessName = element.points_sales.business.reference
          }
          if(element.supplier) {
            suppliersID = element.supplier.id
            suppliersCode = element.supplier.code
            suppliersName = element.supplier.name            
          }       

          if(element.type_voucher) {
            typeVoucherID = element.type_voucher.id
            typeVoucherName = element.type_voucher.name                        
          }

          if(element.points_sales) {
            pointSalesID = element.points_sales.id
            pointSalesName = element.points_sales.name                        
          }          

          this.arr.export.push({
            id: element.id,
            businessID: businessID,
            businessName: businessName,            
            date: element.date,          
            typeVoucherID: typeVoucherID,
            typeVoucherName: typeVoucherName,
            letter: element.letter,
            point_sale: element.point_sale,
            number: element.number,            
            suppliersID: suppliersID,
            suppliersCode: suppliersCode,
            suppliersName: suppliersName,            
            observations: element.observations,
            pointSalesID: pointSalesID,
            pointSalesName: pointSalesName,      
            productID: 0,
            productCode: "",
            productName: "",            
            productCompoundID: 0,
            productCompoundName: "",                                                      
            quantity: 0,
            unitMeasurementID: 0,
            unitMeasurementName: "",                      
          })  

          element.details.forEach(element1 => {
            var productID = 0
            var productCode = ""
            var productName = ""
            var productCompoundID = 0
            var productCompoundName = ""     
            var unitMeasurementID = 0
            var unitMeasurementName = ""               

            if(element1.products_id) {
              productID = element1.products_id
              productCode = element1.products_code
              productName = element1.products_name            
            }

            if(element1.products_colors_id) {
              productID = element1.products_colors_id
              productCode = element1.products_code
              productName = element1.products_name
            }            

            if(element1.products_waist_id) {
              productID = element1.products_waist_id
              productCode = element1.products_code
              productName = element1.products_name
            }            

            if(element1.products_color_waist_id) {
              productID = element1.products_color_waist_id
              productCode = element1.products_code
              productName = element1.products_name
            }       
          
            if(element1.products_compound_id) {
              productCompoundID = element1.products_compound_id
              productCompoundName = element1.products_name
            }    

            if(element1.unit_measurement_id) {
              unitMeasurementID = element1.unit_measurement.id
              unitMeasurementName = element1.unit_measurement.name            
            }            

            this.arr.export.push({
              productID: productID,
              productCode: productCode,
              productName: productName,
              productCompoundID: productCompoundID,
              productCompoundName: productCompoundName,                                                        
              quantity: element1.quantity.replace('.',','),
              unitMeasurementID: unitMeasurementID,
              unitMeasurementName: unitMeasurementName,              
            })
          })          
        });
      },

      // ABM MAIN
      add() {
        this.$refs.purchaseRemitForm.add()
      },
      addFromPurchaseOrder() {
        this.$refs.purchaseRemitForm.addFromPurchaseOrder()
      },
      edit(item) {
        this.$refs.purchaseRemitForm.edit(item)
      },
      remove(item) {
        this.crud.form.id = item.id
        
        var voucher = "ID #" + item.id
        if(item.type_voucher && item.point_sale) {
          voucher = item.type_voucher.name + ' ' + item.point_sale.toString().padStart(4,'0') + '-' + item.number.toString().padStart(8,'0')
        }        

        this.confirmDialog('¿Desea borrar el remito de compra ' + voucher + '?', {
          title: 'Borrar Remito',
          icon: 'delete',
          danger: true,
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {
            let loader = this.$loading.show();
            var result = serviceAPI.eliminarRemitos(this.crud.form.id);

            result.then(() => {      
              this.$refs.purchaseRemitForm.close()        
              loader.hide()
              this.filterPurchaseRemits()
              this.$awn.success("Registro eliminado");
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })
      },          

      // FILTER
      filterLoad() {
        this.filterLoadSuppliers()
        this.filters.date_start = moment().subtract(30, 'days').format('YYYY-MM-DD') 
        this.filters.date_end = moment().format('YYYY-MM-DD') 

        this.restoreFilterStorage()
      },
      filterLoadSuppliers() {
        var result = serviceAPI.obtenerProveedores()        
        result.then((response) => {
          var data = response.data    

          this.arr.filters.suppliers = []
          data.forEach(element => {            
            this.arr.filters.suppliers.push({ code: element.id, label: element.name })            
          });          
        })   
      },       
      filterPurchaseRemits(resetPage=false) {
        if(resetPage) {
          this.table.currentPage = 1
        }

        this.saveFilterStorage()
        this.table.isBusy = true
        var result = serviceAPI.filtrarRemitos(this.filters, this.table.currentPage)        
        result.then((response) => {      
          
          var data = response.data
          this.table.tablePaginate = data

          this.table.items = data.data
          this.arr.purchaseRemits = data.data
          this.prepareExport(data.data)
          
          if(this.itemSelected) {
            this.table.items.forEach(element => {
              if(element.id == this.itemSelected.id) {
                this.openSub(element)
              }
            })
          }

          if(this.table.rowSelected!=null) {            
            this.table.items[this.table.rowSelected]._showDetails = true                   
          }         

          if(this.$refs.table) {            
            this.$refs.table.$forceUpdate()
          }          
          
          this.table.isBusy = false
        })                         
        .catch(error => {                 
          this.table.isBusy = false  
          this.$awn.alert(ErrorToken.valid(error));
        })  
      },        

      // FILTROS SAVE
      saveFilterStorage() {
        Storage.setValue('filter_crud_purchase_remits', JSON.stringify(this.filters))
      },
      restoreFilterStorage() {
        if(Storage.getValue('filter_crud_purchase_remits')) {
          this.filters = JSON.parse(Storage.getValue('filter_crud_purchase_remits'))
        }         
      },             
      
      // COMPROBANTE DETAIL
      openSub(item) {                
        this.itemSelected = item        
        
        this.tableSub.items = item.details      
        this.detailDrawer = true
      },    
      hideSub() {
        this.tableSub.items=[]        
        this.itemSelected = null
        this.detailDrawer = false
      },     
      openDetail() {              
        this.tableSub.items = this.itemSelected.details            
        this.tableDetail.items = this.itemSelected.details            
       
        this.modal.detail._id = Math.random().toString(36).slice(2, 8)
        this.modal.detail.title = "Detalle del Comprobante"
        this.modal.detail.active = true

        let loader = this.$loading.show();
        serviceAPI.mostrarRemitos(this.itemSelected.id)
        .then((response) => {  
          var data = response.data
          if(data && data.details) {
            this.tableDetail.items = this.getRemitDetailsFlat(data.details)
          }
          loader.hide()
        })
        .catch(() => {
          loader.hide()
        })
      },        
      getDetailCode(item) {        
        if(item.products_id || item.products_colors_id || item.products_waist_id || item.products_color_waist_id) {
          return this.getProductCode(item)
        }            
        if(item.products_compound_id) {          
          return item.products_compound.code
        }                
      },
      getDetailName(item) {        
        if(item.products_id || item.products_colors_id || item.products_waist_id || item.products_color_waist_id) {
          return this.getProductName(item)
        }               
        if(item.products_compound_id) {          
          return item.products_compound.name
        }                
      },       
      getProductCode(item) {
        var code = ''
        var prod = null

        if(item.products_code) {
          code = item.products_code
        }          

        if(item.products_id) {
          if(code) {
            return code
          } else {                      
            return item.product.code
          }
        }

        if(item.products_colors_id) {
          prod = item.products_color 
          if(code) {
            return code
          } else {
            return prod.code
          }           
        }

        if(item.products_waist_id) {
          prod = item.products_waist 
          if(code) {
            return code
          } else {
            return prod.code
          }                     
        }

        if(item.products_color_waist_id) {
          prod = item.products_color_waist 
          if(code) {
            return code
          } else {
            return prod.code
          }                     
        }
      },      
      getProductName(item) {
        var name = ''
        var prod = null

        if(item.products_name) {
          name = item.products_name
        }          
        
        if(item.products_id) {
          if(name) {
            return name
          } else {
            return item.product.name
          }          
        }

        if(item.products_colors_id) {
          prod = item.products_color 
          if(name) {
            return name + ' (' + prod.color.name + ')'
          } else {
            return prod.name + ' (' + prod.color.name + ')'
          }                    
        }

        if(item.products_waist_id) {
          prod = item.products_waist           
          if(name) {
            return name + ' (' + prod.waist.name + ')'
          } else {
            return prod.name + ' (' + prod.waist.name + ')'
          }
        }

        if(item.products_color_waist_id) {
          prod = item.products_color_waist 
          if(name) {
            return name + ' (' + prod.color.name + ' - ' + prod.waist.name + ')'
          } else {
            return prod.name + ' (' + prod.color.name + ' - ' + prod.waist.name + ')'
          }          
        }
      },  
      
      // PRINT
      sendPrinter(item) {            
        if(item.deposit_operation) {
          this.crud.print.key=Math.random()
          this.crud.print.id=item.deposit_operation.id
          this.crud.print.reference = item.deposit_operation.reportDefault
        } else {
         this.$awn.alert("No se encontró el movimiento de depósito");
         return false 
        }
      },     

      // PROCESS ADMINISTRATIVE      
      generateInvoice(item) {
        if(!this.hasRemitPrices(item)) {
          this.$awn.alert('El remito tiene ítems sin precio de costo. Editalo para completar los precios antes de generar la factura.')
          return
        }
        this.$router.push({ name: 'PurchaseStaffPurchaseGenerateByRemits', params: { remitsID: item.id }, query: { _t: Date.now() } })
      },      
      isRemitLinked(item) {
        if(!item || !item.details || !item.details.length) {
          return false
        }
        var linked = true
        item.details.forEach(element => {
          if(!element.purchase_id) {
            linked = false
          }
        })
        return linked
      },
      controlsStock(item) {
        if(item && item.control_stock !== undefined && item.control_stock !== null) {
          return !!item.control_stock
        }
        const product = item && item.product ? item.product : null
        if(product && product.control_stock !== undefined && product.control_stock !== null) {
          return !!product.control_stock
        }
        return true
      },
      stockBadgeLabel(item) {
        if(item && item.has_deposit_movement) return 'Movimiento registrado'
        return this.controlsStock(item) ? 'Controla stock · sin movimiento' : 'No controla stock'
      },
      stockBadgeVariant(item) {
        if(item && item.has_deposit_movement) return 'success'
        return this.controlsStock(item) ? 'warning' : 'secondary'
      },
      stockBadgeTitle(item) {
        return item && item.has_deposit_movement
          ? 'Este ítem tiene un movimiento de depósito asociado al remito'
          : (this.controlsStock(item) ? 'El producto controla stock, pero este remito no tiene movimiento asociado' : 'El producto no controla stock')
      },
      hasRemitPrices(item) {
        if(!item || !item.details || !item.details.length) return true
        return item.details.every(element => parseFloat(element.price_cost) > 0)
      },
      hasRemitLinkedItems(item) {
        if(!item || !item.details || !item.details.length) {
          return false
        }
        var linked = false
        item.details.forEach(element => {
          if(element.purchase_id) {
            linked = true
          }
        })
        return linked
      },
      isRemitItemLinked(item) {
        return item && item.purchase_id ? true : false
      },
      isRemitPartiallyLinked(item) {
        if(!item || !item.details || !item.details.length) {
          return false
        }
        return this.hasRemitLinkedItems(item) && !this.isRemitLinked(item)
      },
      getRemitBillingStatusLabel(item) {
        if(!item || !item.details || !item.details.length) {
          return 'Sin items'
        }
        if(!this.hasRemitPrices(item)) {
          return 'Sin precios'
        }
        if(this.isRemitLinked(item)) {
          return 'Factura vinculada'
        }
        if(this.isRemitPartiallyLinked(item)) {
          return 'Parcial'
        }
        return 'Pendiente'
      },
      getPurchaseReference(purchase) {
        if(purchase && purchase.type_voucher && purchase.point_sale) {
          return purchase.type_voucher.name + ' ' + purchase.point_sale.toString().padStart(4,'0') + '-' + purchase.number.toString().padStart(8,'0')
        }
        if(purchase && purchase.id) {
          return 'ID #' + purchase.id
        }
        return ''
      },
      getRemitPurchaseDifferences(remit) {
        const groups = {}
        const details = remit && remit.details ? remit.details : []

        details.forEach(detail => {
          if(!detail.purchase_id || !detail.purchase) {
            return
          }
          // Sólo Factura A expone un neto que puede compararse con el costo
          // neto del remito. En B/C el total de la factura incluye IVA y esta
          // pantalla no tiene la alícuota por ítem para totalizarlo.
          if(detail.purchase.letter != 'A') {
            return
          }
          if(!groups[detail.purchase_id]) {
            groups[detail.purchase_id] = {
              purchase: detail.purchase,
              remitAmount: 0,
            }
          }
          const detailAmount = parseFloat(detail.quantity) * parseFloat(detail.price_cost || 0)
          if(!isNaN(detailAmount)) {
            groups[detail.purchase_id].remitAmount += detailAmount
          }
        })

        return Object.keys(groups)
          .map(key => ({
            purchase: groups[key].purchase,
            remitAmount: groups[key].remitAmount,
            invoiceAmount: parseFloat(groups[key].purchase.amount_net) || 0,
          }))
          .filter(difference => Math.abs(difference.invoiceAmount - difference.remitAmount) > 0.009)
      },
      formatRemitPurchaseAmount(amount) {
        return Intl.NumberFormat('es-AR', { style: 'currency', currency: this.getCurrency || 'ARS' }).format(amount)
      },
      getRemitDetailsFlat(details) {
        var items = []
        if(details && details.products) {
          details.products.forEach(element => {
            items.push(element)
          })
        }
        if(details && details.products_compound) {
          details.products_compound.forEach(element => {
            items.push(element)
          })
        }
        return items
      },
      canShowActions(item) {
        if(!item) {
          return false
        }
        if(!this.hasRemitLinkedItems(item)) {
          return true
        }
        return false
      },
    }    
   
  }
</script>

<style>
  @media (min-width: 1200px) {
    .remit-detail-modal-wide { max-width: min(96vw, 1560px); }
  }
  .table-full-detail-purchase-remits {
    overflow: auto;
    max-height: 350px;    
  }    
  .crud-purchase-remits-price-title {
    font-size: 15px;
    font-weight: 600;
  }
  .crud-purchase-remits-price-code {
    color: gray;
  }
  .purchase-remits-price-cost-divider {
    border-left: 1px solid #c7c7c7;
    text-align: end;        
    padding-top: 5px;
  }  
  .crud-purchase-remits-title-table-price {
    color: #2f353a;
    font-weight: 700;
    padding-right: 10px;
    width: 30%;
    text-align: right
  }  
  .crud-purchase-remits-value-table-price {
    padding-left: 5px;
    text-align: right
  }    
  .purchase-remits-price-cost-update {
    text-align: end; 
    padding-top: 5px;
  }
  .purchase-remits-list-price-cost {
    overflow: auto;
    max-height: 350px;
  }
  .purchase-remits-price-cost-not-related {
    font-size: 10px;
    color: red;
  }
  .purchase-remits-detail-item {
    white-space: normal;
    line-height: 1.2;
  }
  .purchase-remits-detail-item__code {
    color: var(--app-color-text-muted);
    font-size: 12px;
    font-weight: var(--app-font-semibold);
    line-height: 1.15;
  }
  .purchase-remits-detail-item__name {
    color: var(--app-color-text);
    font-weight: var(--app-font-semibold);
    line-height: 1.2;
  }
  .purchase-remits-detail-location {
    white-space: normal;
    line-height: 1.2;
  }
  .purchase-remits-table-footer {
    padding-right: 12px;
  }
  .btn.purchase-remits-action-toggle-labeled {
    display: inline-flex !important;
    align-items: center;
    justify-content: flex-start;
    width: auto !important;
    height: auto !important;
    min-height: 0;
    padding: 0 !important;
    border: none !important;
    background: transparent !important;
    box-shadow: none !important;
    color: var(--app-color-text-secondary) !important;
    font-size: 13px;
    line-height: 1.15;
  }
  .btn.purchase-remits-action-toggle-labeled:hover,
  .btn.purchase-remits-action-toggle-labeled:focus {
    color: var(--app-color-primary) !important;
    text-decoration: underline;
  }
  @media (max-width: 767.98px) {
    .table-purchase-remits-custom.app-table.b-table-stacked-md > tbody > tr > td:last-child::before {
      content: attr(data-label) !important;
    }
  }
</style>
